package er.prateek.crossly.data.model.math

/** Applies an operator, or returns null when the operation is invalid (÷ by zero or non-exact). */
fun MathOp.applyOrNull(a: Int, b: Int): Int? = when (this) {
    MathOp.ADD -> a + b
    MathOp.SUB -> a - b
    MathOp.MUL -> a * b
    MathOp.DIV -> if (b != 0 && a % b == 0) a / b else null
}

private fun MathCell.intOrNull(): Int? = solution.toIntOrNull()

/** Whether an equation's stored solutions actually satisfy its operator. */
fun MathPuzzle.equationHolds(eq: MathEquation): Boolean {
    val a = cells[eq.operandAIndex].intOrNull() ?: return false
    val b = cells[eq.operandBIndex].intOrNull() ?: return false
    val c = cells[eq.resultIndex].intOrNull() ?: return false
    val op = MathOp.fromGlyph(cells[eq.operatorIndex].text) ?: return false
    return op.applyOrNull(a, b) == c
}

/**
 * Whether the puzzle can be solved by pure deduction from the given cells -- no guessing. A
 * number cell becomes known as soon as it is the sole unknown in some equation; the grid is
 * uniquely deducible iff repeating that fills every blank. This is purely structural (depends
 * only on which cells are `given` and how equations overlap), which is exactly the property a
 * good puzzle needs.
 */
fun MathPuzzle.isUniquelyDeducible(): Boolean {
    val allNumbers = numberCellIndices.toSet()
    val known = numberCellIndices.filter { cells[it].given }.toMutableSet()
    var changed = true
    while (changed) {
        changed = false
        for (eq in equations) {
            val unknown = eq.numberIndices.filter { it !in known }
            if (unknown.size == 1) {
                known.add(unknown.single())
                changed = true
            }
        }
    }
    return known.containsAll(allNumbers)
}

enum class MathPuzzleDefect {
    /** Grid size doesn't match width*height, or a number cell has a non-integer solution. */
    MALFORMED,
    /** Some equation's stored solutions don't actually hold. */
    EQUATION_FALSE,
    /** Nothing is left blank for the player to solve. */
    NO_BLANKS,
    /** The givens don't force a unique solution -- the player would have to guess. */
    NOT_DEDUCIBLE,
}

fun MathPuzzle.defects(): Set<MathPuzzleDefect> = buildSet {
    if (cells.size != width * height || width <= 0 || height <= 0) add(MathPuzzleDefect.MALFORMED)
    if (numberCellIndices.any { cells[it].intOrNull() == null || cells[it].solution.toInt() < 0 }) {
        add(MathPuzzleDefect.MALFORMED)
    }
    if (MathPuzzleDefect.MALFORMED !in this) {
        if (equations.isEmpty() || equations.any { !equationHolds(it) }) add(MathPuzzleDefect.EQUATION_FALSE)
        if (numberCellIndices.none { !cells[it].given }) add(MathPuzzleDefect.NO_BLANKS)
        if (!isUniquelyDeducible()) add(MathPuzzleDefect.NOT_DEDUCIBLE)
    }
}

fun MathPuzzle.isWellFormed(): Boolean = defects().isEmpty()
